export function prepareCustomerUpdate(input) {
    const { serviceLocations, ...customerUpdate } = input;
    void serviceLocations;
    return customerUpdate;
}
class CustomerService {
    axiosInstance;
    constructor(axiosInstance) {
        this.axiosInstance = axiosInstance;
    }
    adminPortal = {
        query: async (query) => {
            const { data } = await this.axiosInstance.mainInstance.get(`/customer/admin-portal`, {
                params: query,
            });
            return data;
        },
        fetch: async (id) => {
            const { data } = await this.axiosInstance.mainInstance.get(`/customer/admin-portal/${id}`);
            return data;
        },
        create: async (input) => {
            const { data } = await this.axiosInstance.mainInstance.post(`/customer/admin-portal`, input);
            return data;
        },
        update: async (id, input) => {
            const { data } = await this.axiosInstance.mainInstance.put(`/customer/admin-portal/${id}`, prepareCustomerUpdate(input));
            return data;
        },
        subscriptions: {
            fetch: (id) => {
                return this.axiosInstance.socket.connect(`/customer/admin-portal/${id}`);
            },
        },
        contact: {
            create: async (id, input) => {
                const { data } = await this.axiosInstance.mainInstance.post(`/customer/admin-portal/${id}/contact`, input);
                return data;
            },
            update: async (customerID, contactID, input) => {
                const { data } = await this.axiosInstance.mainInstance.put(`/customer/admin-portal/${customerID}/contact/${contactID}`, input);
                return data;
            },
            delete: async (customerID, contactID) => {
                const { data } = await this.axiosInstance.mainInstance.delete(`/customer/admin-portal/${customerID}/contact/${contactID}`);
                return data;
            },
        },
        googleAds: {
            authenticateGoogle: async () => {
                const response = await this.axiosInstance.mainInstance.get(`/customer/google-ads/oauth/get-auth-url`, {});
                return response;
            },
            googleAuthCallback: async (searchParams) => {
                const response = await this.axiosInstance.mainInstance.get(`/customer/google-ads/oauth/callback-helper${searchParams}`);
                return response;
            },
        },
        leads: {
            query: async (filters) => {
                const { data } = await this.axiosInstance.mainInstance.get(`/customer/alliance-leads`, {
                    params: { ...filters, limit: filters.limit || 10000 },
                });
                return data;
            },
            fetch: async (id) => {
                const { data } = await this.axiosInstance.mainInstance.get(`/customer/alliance-leads/${id}`);
                return data;
            },
            create: async (input) => {
                const { data } = await this.axiosInstance.mainInstance.post(`/customer/alliance-leads`, input);
                return data;
            },
            update: async (id, input) => {
                const { data } = await this.axiosInstance.mainInstance.put(`/customer/alliance-leads/${id}`, input);
                return data;
            },
            subscription: {
                query: (filters) => {
                    return this.axiosInstance.socket.connect('/customer/alliance-leads', {
                        params: filters,
                    });
                },
                fetch: (id) => {
                    return this.axiosInstance.socket.connect(`/customer/alliance-leads/${id}`);
                },
            },
            generateToken: async (id) => {
                const { data } = await this.axiosInstance.mainInstance.post(`/customer/checkout/access-token`, {
                    leadID: id,
                });
                return data;
            },
            internalNote: {
                create: async (id, input) => {
                    const { data } = await this.axiosInstance.mainInstance.post(`/customer/alliance-leads/${id}/internal-note`, input);
                    return data;
                },
            },
            quote: {
                create: async (id, input) => {
                    const { data } = await this.axiosInstance.mainInstance.post(`/customer/alliance-leads/${id}/quote`, input);
                    return data;
                },
                delete: async (leadID, quoteID) => {
                    const { data } = await this.axiosInstance.mainInstance.delete(`/customer/alliance-leads/${leadID}/quote/${quoteID}`);
                    return data;
                },
            },
            haulerQuote: {
                create: async (id, input) => {
                    const { data } = await this.axiosInstance.mainInstance.post(`/customer/alliance-leads/${id}/hauler-quote`, input);
                    return data;
                },
                delete: async (leadID, quoteID) => {
                    const { data } = await this.axiosInstance.mainInstance.delete(`/customer/alliance-leads/${leadID}/hauler-quote/${quoteID}`);
                    return data;
                },
            },
            contactEvent: {
                create: async (id, input) => {
                    const { data } = await this.axiosInstance.mainInstance.post(`/customer/alliance-leads/${id}/contact-event`, input);
                    return data;
                },
            },
        },
        merge: async (input) => {
            const { data } = await this.axiosInstance.appAdminInstance.post(`/customer/admin-portal/merge`, input);
            return data;
        },
        bulkUpdate: async (input) => {
            const { data } = await this.axiosInstance.mainInstance.post(`/customer/admin-portal/bulk-update`, input);
            return data;
        },
        bulkQuery: async (filters) => {
            const { data } = await this.axiosInstance.mainInstance.get(`/customer/admin-portal/bulk-query`, { params: filters });
            return data;
        },
        paymentMethods: {
            create: async (id, input) => {
                const { data } = await this.axiosInstance.mainInstance.post(`/customer/${id}/payment-methods`, input);
                return data;
            },
            /** `includeStatuses` adds flagged statuses; chargeable ones always come back. */
            list: async (id, type, includeStatuses) => {
                const { data } = await this.axiosInstance.mainInstance.get(`/customer/${id}/payment-methods`, { params: { type, includeStatuses: includeStatuses?.length ? includeStatuses.join(',') : undefined } });
                return data;
            },
            updateStatus: async (customerID, customerPaymentMethodID, input) => {
                const { data } = await this.axiosInstance.mainInstance.patch(`/customer/${customerID}/payment-methods/${customerPaymentMethodID}`, input);
                return data;
            },
            /** Addressed by the association row ID, which survives a Card Account Updater replacement. */
            updateBillingContact: async (id, customerPaymentMethodID, input) => {
                const { data } = await this.axiosInstance.mainInstance.put(`/customer/${id}/payment-methods/${customerPaymentMethodID}/billing-contact`, input);
                return data;
            },
        },
    };
    appAdmin = {
        updateCustomer: async (id, customer) => {
            const { data } = await this.axiosInstance.appAdminInstance.put(`/customer/admin-portal/${id}`, prepareCustomerUpdate(customer));
            return data;
        },
        import: {
            preview: async (haulerID, input) => {
                const { data } = await this.axiosInstance.appAdminInstance.post(`/customer/app-admin/${haulerID}/import/preview`, input);
                return data;
            },
            execute: async (haulerID, input) => {
                const { data } = await this.axiosInstance.appAdminInstance.post(`/customer/app-admin/${haulerID}/import`, input);
                return data;
            },
        },
    };
    /**
     * Represents the customer portal functionality of the Customer service.
     */
    customerPortal = {
        /**
         * Fetches the customer profile from the customer portal.
         * @returns A promise that resolves to the customer profile.
         */
        fetch: async () => {
            const { data } = await this.axiosInstance.mainInstance.get('/customer/customer-portal/profile');
            return data;
        },
        /**
         * Updates the customer profile in the customer portal.
         * @param account - The updated customer profile.
         * @returns A promise that resolves to the updated customer profile.
         */
        update: async (account) => {
            const { data } = await this.axiosInstance.mainInstance.put('/customer/customer-portal/profile', account);
            return data;
        },
        leads: {
            create: async (input) => {
                const { data } = await this.axiosInstance.mainInstance.post('/customer/alliance-leads/public', input);
                return data;
            },
        },
        // Only expose methods whose underlying routes accept the 'customer' role.
        // list + create accept ['alliance-customer-service', 'customer'] server-side.
        // updateBillingContact / update are alliance-customer-service-only,
        // so we deliberately do NOT re-export them on customer-portal to prevent customer-portal
        // callers from type-checking against admin-only endpoints they'd 403 on at runtime.
        paymentMethods: {
            create: this.adminPortal.paymentMethods.create,
            list: this.adminPortal.paymentMethods.list,
        },
    };
    wayste = {
        fetch: async (id) => {
            const { data } = await this.axiosInstance.mainInstance.get(`/customer/${id}`);
            return data;
        },
        query: async (query) => {
            const { data } = await this.axiosInstance.mainInstance.get('/customer', {
                params: query,
            });
            return data;
        },
        create: async (input) => {
            const { data } = await this.axiosInstance.mainInstance.post('/customer', input);
            return data;
        },
        update: async (id, input) => {
            const { data } = await this.axiosInstance.mainInstance.put(`/customer/${id}`, prepareCustomerUpdate(input));
            return data;
        },
        addServiceLocation: async (customerID, input) => {
            const { data } = await this.axiosInstance.mainInstance.post(`/customer/${customerID}/service-location`, input);
            return data;
        },
        removeServiceLocation: async (customerID, serviceLocationID) => {
            await this.axiosInstance.mainInstance.delete(`/customer/${customerID}/service-location/${serviceLocationID}`);
        },
        contact: {
            create: async (customerID, input) => {
                const { data } = await this.axiosInstance.mainInstance.post(`/customer/wayste/${customerID}/customer-contact`, input);
                return data;
            },
            update: async (customerID, contactID, input) => {
                const { data } = await this.axiosInstance.mainInstance.patch(`/customer/wayste/${customerID}/customer-contact/${contactID}`, input);
                return data;
            },
            delete: async (customerID, contactID) => {
                const { data } = await this.axiosInstance.mainInstance.delete(`/customer/wayste/${customerID}/customer-contact/${contactID}`);
                return data;
            },
        },
        signableDocument: {
            fetch: async (id) => {
                const { data } = await this.axiosInstance.mainInstance.get(`/customer/wayste/signable-document/${id}`);
                return data;
            },
            create: async (input) => {
                const { data } = await this.axiosInstance.mainInstance.post(`/customer/wayste/signable-document`, input);
                return data;
            },
            update: async (id, input) => {
                const { data } = await this.axiosInstance.mainInstance.patch(`/customer/wayste/signable-document/${id}`, input);
                return data;
            },
            query: async (query) => {
                const { data } = await this.axiosInstance.mainInstance.get(`/customer/wayste/signable-document`, {
                    params: query,
                });
                return data;
            },
        },
        waysteCustomerDocument: {
            fetch: async (id) => {
                const { data } = await this.axiosInstance.mainInstance.get(`/customer/wayste/customer-document/${id}`);
                return data;
            },
            create: async (input) => {
                const { data } = await this.axiosInstance.mainInstance.post(`/customer/wayste/customer-document`, input);
                return data;
            },
            query: async (query) => {
                const { data } = await this.axiosInstance.mainInstance.get(`/customer/wayste/customer-document`, {
                    params: query,
                });
                return data;
            },
        },
        merge: async (input) => {
            const { data } = await this.axiosInstance.appAdminInstance.post('/customer/merge', input);
            return data;
        },
    };
    waysteDashboard = {
        authenticate: async (token) => {
            const { data } = await this.axiosInstance.mainInstance.get(`customer/wayste-dashboard-token/${token}`);
            if (data.authority) {
                this.axiosInstance.mainInstance.defaults.headers.common.Authorization = `Bearer ${data.authority.token}`;
            }
            return data;
        },
        waysteCustomerDocument: {
            fetch: async (id) => {
                const { data } = await this.axiosInstance.mainInstance.get(`/customer/wayste-dashboard/customer-document/${id}`);
                return data;
            },
            update: async (id, input) => {
                const { data } = await this.axiosInstance.mainInstance.patch(`/customer/wayste-dashboard/customer-document/${id}`, input);
                return data;
            },
            query: async (query) => {
                const { data } = await this.axiosInstance.mainInstance.get(`/customer/wayste-dashboard/customer-document`, {
                    params: query,
                });
                return data;
            },
        },
    };
}
export default CustomerService;
//# sourceMappingURL=Customer.service.js.map